// legalVersions.js -- current Terms/Privacy/age versions (spec D7). Single job: the deploy's
// public/legal-versions.json when valid within 3 s (headers AND body), else the last good remote
// copy the phone saved, else the bundled copy. Never rejects.
import { PUBLIC_WEB_BASE } from '../../lib/api';
import * as Sentry from '../../lib/errorReporting';

// Must equal public/legal-versions.json and the pages' Effective Date (drift test).
export const BUNDLED_LEGAL_VERSIONS = Object.freeze({ terms: '2026-10-16', privacy: '2026-10-27', age: 'age-v1' });
const DATE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
// The DB version column is ^[a-z0-9.-]{1,32}$: 'age-v' plus at most 27 digits.
const AGE = /^age-v[0-9]{1,27}$/;

export function isValidLegalVersions(v) {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  if (Object.keys(v).sort().join(',') !== 'age,privacy,terms') return false;
  return typeof v.terms === 'string' && DATE.test(v.terms)
    && typeof v.privacy === 'string' && DATE.test(v.privacy)
    && typeof v.age === 'string' && AGE.test(v.age);
}

async function attempt(fetchImpl, signal) {
  const res = await fetchImpl(`${PUBLIC_WEB_BASE}/legal-versions.json`, { cache: 'no-store', signal });
  if (!res || !res.ok) return { reason: `http_${res && res.status}` };
  const type = (res.headers && res.headers.get('content-type')) || '';
  if (!type.includes('application/json')) return { reason: 'content_type' };
  let body;
  try { body = JSON.parse(await res.text()); } catch { return { reason: 'parse' }; }
  if (!isValidLegalVersions(body)) return { reason: 'shape' };
  return { versions: Object.freeze({ terms: body.terms, privacy: body.privacy, age: body.age }) };
}

export async function fetchLegalVersions({ fetchImpl = (...a) => fetch(...a), timeoutMs = 3000, lastGood = null } = {}) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => { if (controller) controller.abort(); resolve({ reason: 'timeout' }); }, timeoutMs);
  });
  let out;
  try {
    out = await Promise.race([attempt(fetchImpl, controller && controller.signal), timeout]);
  } catch {
    out = { reason: 'network' };
  } finally {
    clearTimeout(timer);
  }
  if (out.versions) return { versions: out.versions, source: 'remote' };
  Sentry.captureMessage('onboarding: legal-versions fallback', { level: 'warning', tags: { function: 'fetchLegalVersions', reason: out.reason } });
  if (isValidLegalVersions(lastGood)) return { versions: lastGood, source: 'last_good' };
  return { versions: BUNDLED_LEGAL_VERSIONS, source: 'bundled' };
}
